/**
 * useSafeBack — role-aware safe back navigation.
 *
 * Replaces unsafe `navigate(-1)` calls that walk browser history and can
 * loop the user back into the very page they just came from (classic
 * Marketplace ↔ Profile loop, Passport ↔ Inbox loop, etc.).
 *
 * Resolution order:
 *   1. Explicit `fallbackPath` arg (per-call override).
 *   2. `location.state.fromPath` if the source page passed it forward.
 *   3. Role-based home:
 *        - learner → /dashboard
 *        - adi/pdi/admin → /instructor
 *        - logged-out / unknown → /
 *
 * Returns a stable callback `goBack(stateOverride?)` that takes an
 * optional state object to forward to the destination (e.g. preserving
 * filter state when returning to a search page).
 *
 * Scope-locked: this hook only fixes navigation safety. It does not
 * redesign UX or add new product behaviour.
 */
import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export function safeBackPath(user, override) {
  if (override) return override;
  if (!user) return "/";
  const role = user.role;
  if (role === "learner") return "/dashboard";
  if (role === "adi" || role === "pdi" || role === "admin") return "/instructor";
  return "/";
}

export function useSafeBack(fallbackPath) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  return useCallback(
    (state) => {
      const fromState = location.state && location.state.fromPath;
      const target = safeBackPath(user, fallbackPath || fromState);
      navigate(target, state ? { state } : undefined);
    },
    [navigate, location.state, user, fallbackPath]
  );
}

export default useSafeBack;
